<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="application?.applicant_name"
      [canGoBack]="!windowService.isDesktopRes" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper [class]="isRightSideContent ? 'right-side-scroll-enabled' : ''">

    @if (errorMessage) {
      <tidy-error-message [text]="errorMessage" />
    }

    @if (loaded && application) {

      @if (showPolicyBanner) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="policyBannerText" [translate]="false" />
          </tidy-row>
        </tidy-alert>
      }

      <!-- Applicant summary -->
      <tidy-card>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [header]="application.applicant_name" [translate]="false" />
          <tidy-text
            [body]="application.status"
            [translate]="false"
            [ngStyle]="{ color: getRecommendationColor() }" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/mail-outline.svg'" class="body-size" />
          <tidy-text [body]="application.email" [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/call-outline.svg'" class="body-size" />
          <tidy-text [body]="application.phone" [translate]="false" />
        </tidy-row>
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/home-outline.svg'" class="body-size" />
          <tidy-text [body]="application.property_name + ' · ' + application.unit" [translate]="false" />
        </tidy-row>
        @if (isByRoomUnit) {
          <tidy-row class="vertical-align-center">
            <tidy-image [src]="'assets/svg/key-outline.svg'" class="body-size" />
            <tidy-text [body]="'Applying for ' + application.unit" [translate]="false" />
          </tidy-row>
        }
        <tidy-row class="vertical-align-center">
          <tidy-image [src]="'assets/svg/calendar-outline.svg'" class="body-size" />
          <tidy-text
            [body]="'Move-in ' + (application.desired_move_in | date:'MMM d, yyyy') + ' · ' + application.lease_term + ' · $' + application.monthly_rent.toLocaleString('en-US') + '/mo'"
            [translate]="false" />
        </tidy-row>
        <tidy-row>
          <tidy-text [helper]="'via ' + application.source" [translate]="false" />
        </tidy-row>
      </tidy-card>

      <!-- Tenant screening report -->
      <tidy-row>
        <tidy-text [title]="'Tenant Screening'" />
      </tidy-row>

      <tidy-card>
        <tidy-row class="vertical-align-center">
          <div [ngStyle]="{'width': '12px', 'height': '12px', 'border-radius': '50%', 'background-color': getRecommendationColor(), 'margin-right': '8px', 'flex-shrink': '0'}"></div>
          <tidy-text
            [header]="getRecommendationLabel()"
            [translate]="false"
            [ngStyle]="{ color: getRecommendationColor() }" />
        </tidy-row>

        <tidy-divider />

        <tidy-row class="extra-top-padding" [alignLastItemRight]="true">
          <tidy-text [body]="'Credit Score'" />
          <tidy-text
            [body]="application.screening.credit_score !== null ? (application.screening.credit_score + '  ·  ' + getCreditBand(application.screening.credit_score)) : 'Pending'"
            [translate]="false" />
        </tidy-row>

        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Income Verification'" />
          <tidy-text
            [body]="application.screening.income_verified ? ('Verified · ' + (application.screening.income_to_rent_ratio | number:'1.1-1') + 'x rent') : 'Not verified'"
            [translate]="false"
            [ngStyle]="{ color: application.screening.income_verified ? '#2E7D32' : '#999' }" />
        </tidy-row>

        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Criminal Background'" />
          <tidy-text
            [body]="getCheckLabel(application.screening.criminal)"
            [translate]="false"
            [ngStyle]="{ color: getCheckColor(application.screening.criminal) }" />
        </tidy-row>

        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Eviction History'" />
          <tidy-text
            [body]="getCheckLabel(application.screening.eviction)"
            [translate]="false"
            [ngStyle]="{ color: getCheckColor(application.screening.eviction) }" />
        </tidy-row>

        <tidy-divider />

        <tidy-row class="extra-top-padding">
          <tidy-text
            [helper]="'Screening by ' + application.screening.provider + '. Reports are FCRA-regulated — an adverse action notice is sent automatically on decline.'"
            [translate]="false" />
        </tidy-row>
      </tidy-card>

      <!-- Application details -->
      <tidy-row>
        <tidy-text [title]="'Application Details'" />
      </tidy-row>

      <tidy-card>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Employer'" />
          <tidy-text [body]="application.employer" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Job Title'" />
          <tidy-text [body]="application.job_title" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Monthly Income'" />
          <tidy-text [body]="'$' + application.monthly_income.toLocaleString('en-US')" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Current Address'" />
          <tidy-text [body]="application.current_address" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Occupants'" />
          <tidy-text [body]="application.occupants.toString()" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Pets'" />
          <tidy-text [body]="application.pets" [translate]="false" />
        </tidy-row>
        <tidy-row [alignLastItemRight]="true">
          <tidy-text [body]="'Application Fee'" />
          <tidy-text
            [body]="application.application_fee_paid ? 'Paid' : 'Unpaid'"
            [translate]="false"
            [ngStyle]="{ color: application.application_fee_paid ? '#2E7D32' : '#999' }" />
        </tidy-row>
      </tidy-card>

      <!-- Actions -->
      <tidy-row class="extra-top-padding">
        <tidy-button
          [text]="'Approve & Send Lease for E-Signature'"
          [action]="sendLeaseAction"
          class="primary" />
      </tidy-row>
      <tidy-row>
        <tidy-button
          [text]="'Approve'"
          [action]="approveAction"
          class="secondary" />
      </tidy-row>
      <tidy-row>
        <tidy-text
          [actionBody]="'Decline Application'"
          (action)="decline()"
          class="link red" />
      </tidy-row>
    }
  </tidy-wrapper>
</ng-template>

@if (!isRightSideContent) {
  <app-scroll-wrapper [templateContent]="content" />
}

@if (isRightSideContent) {
  <ion-content class="ion-content-right-column">
    <ng-container *ngTemplateOutlet="content" />
  </ion-content>
}
